import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useMutation } from '@apollo/client';

import { useAuth } from '@/helpers/authUtils';
import { CLAIM_BOOKING } from '@/helpers/claimBookingSchema';
import { logError } from '@/helpers/logger/logUtils';
import useReCaptchaCallback from '@/hooks/useRecaptchaCallback';
import useReCaptchaContext from '@/hooks/useReCaptchaContext';
import { selectFeatureFlags } from '@/reducers/common/selectors';

import styles from './ReactivationFlowContainer.module.scss';

import {
  ClaimBookingDetailsConfirmation,
  ClaimBookingDobUpdate,
  ClaimBookingEmailSelect,
  ClaimBookingError,
  ClaimBookingForm,
  ClaimBookingSailorSelect,
  ClaimBookingSuccess,
  ClaimImportError,
  ClaimImportingInProgress,
  ReclaimAccount,
} from './steps';

import Spinner from '../Spinner';
import { WizardContainer } from '../Wizard';

import {
  RECLAIM_ACCOUNT,
  DETAILS_MANUAL_ENTRY_FORM,
  SELF_SERVE_ARRAY,
  EMAIL_MISMATCH,
  BOOKING_NOT_FOUND,
  CLAIMED,
  CLAIMED_COMPLETED,
  IMPORT_IN_PROGRESS,
  IMPORT_ERROR,
  UPDATE_DOB,
  READY_TO_CLAIM,
} from './steps/constants';

const useHandledMutation = (mutation, options, onError) => {
  const [mutationHandler, { loading, error }] = useMutation(mutation, {
    ...options,
  });

  useEffect(() => {
    if (error) {
      onError();
      error.graphQLErrors.map((gqlError) => logError(gqlError));
    }
  }, [error, onError]);

  return [mutationHandler, { loading }];
};

const ReactivationFlowContainer = ({ backToLogin }) => {
  const { isLoaded: isReCaptchaLoaded } = useReCaptchaContext();
  const [step, setStep] = useState({ stepId: RECLAIM_ACCOUNT, data: { sailors: [] } });
  const [claimBooking, { loading }] = useHandledMutation(CLAIM_BOOKING, null, backToLogin);

  const { blankEmailCheckEnabled } = useSelector(selectFeatureFlags);
  const Auth = useAuth();
  const [currentUserAuth, setCurrentUserAuth] = useState(null);
  const claimConfirmedStatuses = [READY_TO_CLAIM, CLAIMED];
  const isLoading = loading || !isReCaptchaLoaded;

  const getSelectedSailor = (selectedSailorClientId) => {
    if (!selectedSailorClientId) return null;
    return step?.data?.sailors?.find((sailor) => sailor?.clientId === selectedSailorClientId);
  };

  const handleBirthDateChange = (data) => {
    handleProceed(CLAIMED)(data);
  };

  const handleClaimSubmitWithRecaptcha = useReCaptchaCallback({
    action: 'claimV2',
    callback: async (rcToken, value) => {
      await handleClaimSubmit(value, rcToken);
    },
  });

  const handleSailorSelect = (selectedSailorData) => {
    if (selectedSailorData?.user?.birthday) {
      return handleClaimSubmitWithRecaptcha(selectedSailorData);
    }

    const selectedSailorClientId = selectedSailorData?.user?.clientID;
    const selectedSailor = getSelectedSailor(selectedSailorClientId);

    if (!selectedSailor) return null;

    const hasMatchedSailorEmail = !!selectedSailor?.email;
    const status = hasMatchedSailorEmail ? EMAIL_MISMATCH : UPDATE_DOB;

    const updatedSailorData = {
      ...step.data,
      sailors: selectedSailor ? [selectedSailor] : [],
      status,
      firstName: selectedSailor?.name?.first || '',
    };
    return handleNextStep(status, { ...updatedSailorData });
  };

  const addFirstNameToUserData = (value, firstName) => {
    if (!value.user || value.user.firstName || !firstName) return value;
    return {
      ...value,
      user: {
        ...value.user,
        firstName,
      },
    };
  };

  useEffect(() => {
    const fetchAuth = async () => {
      try {
        const auth = await Auth.getAuth();

        setCurrentUserAuth(auth);
      } catch (error) {
        logError(error);
      }
    };
    fetchAuth();
  }, [Auth]);

  const handleNextStep = (stepId, data = {}) => {
    setStep((prevStep) => ({ ...prevStep, stepId, data }));
  };

  const handleClaimSubmit = async (value, rcToken) => {
    const valueWithFirstName = addFirstNameToUserData(value, step.data?.firstName);

    const newValue = {
      ...valueWithFirstName,
      blankEmailCheckEnabled,
    };

    const { data } = await claimBooking({
      variables: { value: newValue },
      context: {
        headers: {
          'X-Recaptcha-Token': rcToken,
        },
      },
    });

    if (!data?.claim) {
      return;
    }

    handleStepTransition(data.claim, value.bookingNumber);
  };

  const handleStepTransition = (claimData, bookingNumber) => {
    if (step.stepId === SELF_SERVE_ARRAY && claimConfirmedStatuses?.includes(claimData?.status)) {
      if (!claimData.sailors?.[0]?.dob) {
        handleNextStep(UPDATE_DOB, { ...claimData, bookingNumber });
      } else {
        handleNextStep(CLAIMED, { ...claimData, bookingNumber });
      }
      return;
    }
    if (claimConfirmedStatuses?.includes(claimData?.status) && !claimData.sailors?.[0]?.dob) {
      handleNextStep(UPDATE_DOB, { ...claimData, bookingNumber });
      return;
    }
    handleNextStep(claimData.status, { ...claimData, bookingNumber });
  };

  const handleProceed = (stepId) => (value) => {
    const isDateOfBirthMissing = !value?.selectedDOB && !value?.sailors?.[0]?.dob;
    const needsDobUpdate = stepId === CLAIMED && isDateOfBirthMissing;
    const nextStep = needsDobUpdate ? UPDATE_DOB : stepId;

    handleNextStep(nextStep, {
      ...value,
      reservationDetails: step.data?.reservationDetails,
      firstName: value?.firstName || step.data?.firstName,
      noOfGuests: value?.noOfGuests || step.data?.noOfGuests,
    });
  };

  const reloadReservation = () => {
    backToLogin();
  };

  const connectBooking = () => {
    handleNextStep(DETAILS_MANUAL_ENTRY_FORM);
  };
  return (
    <div id="ReactivationFlowContainer" className={styles.ReactivationFlowContainer}>
      <WizardContainer selectedStepId={step.stepId} className={styles.content}>
        <ReclaimAccount
          tabId={RECLAIM_ACCOUNT}
          onSubmit={handleProceed(DETAILS_MANUAL_ENTRY_FORM)}
          onBackClick={backToLogin}
        />
        <ClaimBookingForm
          tabId={DETAILS_MANUAL_ENTRY_FORM}
          onSubmit={handleClaimSubmitWithRecaptcha}
          onCancel={backToLogin}
        />
        <ClaimBookingSailorSelect
          tabId={SELF_SERVE_ARRAY}
          data={step.data}
          onSubmit={handleSailorSelect}
          onCancel={backToLogin}
        />
        <ClaimBookingEmailSelect
          tabId={EMAIL_MISMATCH}
          data={step.data}
          currentUser={currentUserAuth}
          onSubmit={handleProceed(CLAIMED)}
          onCancel={backToLogin}
        />
        <ClaimBookingDetailsConfirmation
          tabId={CLAIMED}
          data={step.data}
          onSubmit={handleClaimSubmitWithRecaptcha}
          onCancel={backToLogin}
          currentUser={currentUserAuth}
        />
        <ClaimBookingDobUpdate
          tabId={UPDATE_DOB}
          onNext={handleBirthDateChange}
          onCancel={backToLogin}
          data={step.data}
        />
        <ClaimBookingSuccess tabId={CLAIMED_COMPLETED} onClickLogin={backToLogin} />
        <ClaimBookingError tabId={BOOKING_NOT_FOUND} onClose={backToLogin} />
        <ClaimImportingInProgress tabId={IMPORT_IN_PROGRESS} reloadReservation={reloadReservation} />
        <ClaimImportError tabId={IMPORT_ERROR} connectBooking={connectBooking} />
      </WizardContainer>
      {isLoading && (
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default ReactivationFlowContainer;
